/**
 * Stock Levels — what every tracked product has on hand right now.
 *
 * Reads the derived cache the stock sync lambda maintains on ProductTable
 * (`stockOnHand`, `soldOut`, `stockUpdatedAt`); the ledger behind it is the
 * source of truth. Nothing here writes a count directly — Counted and
 * Received append events and let the cloud recompute, exactly as a till
 * does, so the dashboard can never disagree with the terminals.
 *
 * A product tracked per variant (GRN/variants spec 5.5) shows the SUM of its
 * variants and "N of M variants sold out", and expands into one sub-row per
 * variant with its own Counted, Waste, Received and History. The product row
 * itself takes no count: the cloud refuses a product-level event for such a
 * product, so every movement names a variant.
 *
 * See docs/feature-stock-management-implementation.md §7.2.
 */
import React, {useEffect, useMemo, useState} from 'react';
import {connect} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {get} from 'lodash';
import {Actions} from '../../../internals/app/Actions';
import StockEventRepository, {
  StockEvent,
} from '../../../internals/repository/StockEventRepository';
import StockAdjustModal, {AdjustMode} from './StockAdjustModal';
import StockHistoryModal from './StockHistoryModal';
import IngredientsTab, {type IngredientRow} from './IngredientsTab';
import {
  DEFAULT_DAY_START,
  dayStartOf,
  isSameTradingDay,
} from '../../../internals/managers/ShopSettingsManager';
import {stepFor, type StockItem} from '../../../internals/repository/StockItemRepository';
import {soldOutSummary, variantRows, type VariantRow} from '../../product/variantStock';

const MODE_LABEL: Record<string, string> = {
  unit: 'Count units',
  prep: 'Daily prep',
  recipe: 'Recipe',
};

type StatusFilter = 'all' | 'ok' | 'low' | 'sold_out' | 'stale';
type ModeFilter = 'all' | 'unit' | 'prep' | 'recipe';

interface Row {
  pid: string;
  productName: string;
  stockMode: string;
  onHand: number;
  parLevel: number | null;
  soldOut: boolean;
  updatedAt?: string;
  status: Exclude<StatusFilter, 'all'>;
  /** Per-variant stock; the figures above are then the derived summary. */
  trackByVariant: boolean;
  variants: VariantRow[];
}

/**
 * A variant-tracked product's status, from its variants: sold out only when
 * the cloud says every variant is, low when any variant is low or out.
 * Uncounted variants do not count against it.
 */
function variantProductStatus(product: any, variants: VariantRow[]): Row['status'] {
  if (get(product, 'soldOut') === true) return 'sold_out';
  if (variants.some((v) => v.status === 'low' || v.status === 'sold_out')) return 'low';
  return 'ok';
}

/**
 * Status mirrors the rule the tills use: sold out at or below zero, low at
 * or below par. Par is a unit-mode idea — a prep item is replaced each
 * morning, so "below par" says nothing useful about it.
 */
function statusOf(product: any, dayStart?: string | null): Row['status'] {
  const onHand = Number(get(product, 'stockOnHand', 0)) || 0;

  /**
   * A prep count belongs to the trading day it was set on (H3). Yesterday's
   * zero is not today's sold out: the kitchen simply has not prepped yet, and
   * painting every prep item red each morning taught owners to ignore the
   * column. The till treats such a count as inactive for the same reason.
   */
  if (get(product, 'stockMode') === 'prep') {
    const countedAt = get(product, 'stockBaselineAt') || get(product, 'stockUpdatedAt');
    if (!countedAt || !isSameTradingDay(countedAt, new Date(), dayStart || DEFAULT_DAY_START)) {
      return 'stale';
    }
  }

  if (get(product, 'soldOut') === true || onHand <= 0) return 'sold_out';
  const par = get(product, 'parLevel', null);
  if (get(product, 'stockMode') === 'unit' && par != null && onHand <= Number(par)) {
    return 'low';
  }
  return 'ok';
}

/**
 * Pull the product array out of whatever the store hands us.
 *
 * `state.product.get('products')` is NOT an array and NOT an Immutable
 * collection — it is a plain `{products, nextLastKey, totalCount}` wrapper,
 * which is why ProductList and ProductGrid both read `products.products`.
 * Reaching for `.toJS()` here silently produced an empty list and an always
 * empty Stock Levels table. The array and Immutable forms are kept as
 * fallbacks so a future reducer change degrades instead of blanking.
 */
export function productList(products: any): any[] {
  if (Array.isArray(products)) return products;
  if (Array.isArray(products?.products)) return products.products;
  const asJs = products?.toJS?.();
  if (Array.isArray(asJs)) return asJs;
  if (Array.isArray(asJs?.products)) return asJs.products;
  return [];
}

/** Tracked products, as the Stock Levels table renders them. */
export function toStockRows(products: any, dayStart?: string | null): Row[] {
  return productList(products)
    .filter((p: any) => {
      const mode = get(p, 'stockMode', 'none');
      return mode && mode !== 'none';
    })
    .map((p: any) => {
      const trackByVariant = get(p, 'trackByVariant') === true;
      const variants = trackByVariant ? variantRows(p) : [];
      return {
      pid: p.pid,
      // Defaulted, because the sort below compares these. A record that has
      // lost its name — an import that omitted it, or an attribute dropped by
      // a whole-item write — used to throw out of this function and blank the
      // whole screen. See docs/stock-test-findings.md H1.
      productName: get(p, 'productName', '') || '',
      stockMode: get(p, 'stockMode', 'none'),
      onHand: Number(get(p, 'stockOnHand', 0)) || 0,
      parLevel: get(p, 'parLevel', null),
      soldOut: get(p, 'soldOut') === true,
      updatedAt: get(p, 'stockUpdatedAt'),
      status: trackByVariant ? variantProductStatus(p, variants) : statusOf(p, dayStart),
      trackByVariant,
      variants,
      };
    })
    .sort((a: Row, b: Row) => (a.productName ?? '').localeCompare(b.productName ?? ''));
}

const STATUS_STYLE: Record<Row['status'], string> = {
  ok: 'bg-emerald-50 text-emerald-700',
  low: 'bg-amber-50 text-amber-700',
  sold_out: 'bg-red-50 text-red-700',
  stale: 'bg-gray-100 text-gray-600',
};

const STATUS_LABEL: Record<Row['status'], string> = {
  ok: 'OK',
  low: 'Low',
  sold_out: 'Sold out',
  stale: 'Not counted today',
};

const StockLevelsView = (props: any) => {
  const {
    products,
    ingredients,
    suppliers,
    selectedShop,
    fetchProducts,
    fetchIngredients,
    fetchSuppliers,
    createIngredient,
    updateIngredient,
    deactivateIngredient,
  } = props;
  const shopId = selectedShop?.shopId;

  const navigate = useNavigate();
  const [tab, setTab] = useState<'products' | 'ingredients'>('products');
  const [adjustingIngredient, setAdjustingIngredient] =
    useState<{row: IngredientRow; mode: AdjustMode} | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [modeFilter, setModeFilter] = useState<ModeFilter>('all');
  const [search, setSearch] = useState('');
  const [adjusting, setAdjusting] = useState<{row: Row; mode: AdjustMode} | null>(null);
  const [history, setHistory] = useState<{row: Row; events: StockEvent[]} | null>(null);
  /** Products whose variant sub-rows are open. */
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [adjustingVariant, setAdjustingVariant] =
    useState<{row: Row; variant: VariantRow; mode: AdjustMode} | null>(null);
  const toggleExpanded = (pid: string) =>
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{kind: 'ok' | 'err'; text: string} | null>(null);

  useEffect(() => {
    if (shopId) fetchProducts(shopId, 500, null, true);
  }, [shopId, fetchProducts]);

  useEffect(() => {
    if (shopId) fetchIngredients(shopId, true);
  }, [shopId, fetchIngredients]);

  // For the ingredient form's Default supplier picker.
  useEffect(() => {
    if (shopId) fetchSuppliers(shopId);
  }, [shopId, fetchSuppliers]);

  const flash = (kind: 'ok' | 'err', text: string) => {
    setNotice({kind, text});
    setTimeout(() => setNotice(null), 4000);
  };

  // The shop's trading-day start, so a prep count set last night still reads
  // as today's at 01:00 (H3).
  const dayStart = useMemo(() => dayStartOf(selectedShop), [selectedShop]);
  const rows: Row[] = useMemo(
    () => toStockRows(products, dayStart),
    [products, dayStart],
  );

  const visible = rows.filter((r) => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (modeFilter !== 'all' && r.stockMode !== modeFilter) return false;
    if (search && !r.productName.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const openHistory = async (row: Row) => {
    setBusy(true);
    try {
      const events = await StockEventRepository.getProductHistory(shopId, row.pid, 50);
      setHistory({row, events});
    } catch (e: any) {
      flash('err', e.message || 'Could not load history');
    } finally {
      setBusy(false);
    }
  };

  const openVariantHistory = async (row: Row, variant: VariantRow) => {
    setBusy(true);
    try {
      const events = await StockEventRepository.getVariantHistory(
        shopId,
        row.pid,
        variant.variantId,
        50,
      );
      setHistory({row: {...row, productName: `${row.productName} · ${variant.label}`}, events});
    } catch (e: any) {
      flash('err', e.message || 'Could not load history');
    } finally {
      setBusy(false);
    }
  };

  const openIngredientHistory = async (row: IngredientRow) => {
    setBusy(true);
    try {
      const events = await StockEventRepository.getIngredientHistory(shopId, row.stockId, 50);
      setHistory({row: {productName: row.itemName} as any, events});
    } catch (e: any) {
      flash('err', e.message || 'Could not load history');
    } finally {
      setBusy(false);
    }
  };

  /**
   * After an ingredient moves, BOTH lists are stale: the ingredient's own
   * figure, and every dish whose portions the cloud just recomputed.
   */
  const onIngredientAdjusted = (message: string) => {
    setAdjustingIngredient(null);
    flash('ok', message);
    if (!shopId) return;
    fetchIngredients(shopId, true);
    fetchProducts(shopId, 500, null, true);
  };

  const onAdjusted = (message: string) => {
    setAdjusting(null);
    flash('ok', message);
    // The cache is rewritten by the lambda before it answers, so a refetch
    // shows the new figure rather than the one we just replaced.
    if (shopId) fetchProducts(shopId, 500, null, true);
  };

  const counts = {
    all: rows.length,
    ok: rows.filter((r) => r.status === 'ok').length,
    low: rows.filter((r) => r.status === 'low').length,
    sold_out: rows.filter((r) => r.status === 'sold_out').length,
    stale: rows.filter((r) => r.status === 'stale').length,
  };

  return (
    <div className="h-screen flex flex-col w-full p-6">
      <div className="flex-shrink-0 pb-5">
        <h1 className="text-xl font-semibold text-text1">Stock Levels</h1>
        <p className="text-sm text-text2 mt-1">
          Counts are derived from the till ledger. Adjusting one here records an
          event, the same way a terminal does.
        </p>
      </div>

      {notice && (
        <div
          className={`mb-4 rounded-lg px-4 py-2.5 text-sm ${
            notice.kind === 'ok'
              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {notice.text}
        </div>
      )}

      <div className="flex gap-1 border-b border-borderc pb-0 mb-4">
        {(['products', 'ingredients'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              tab === t
                ? 'border-brand text-brand'
                : 'border-transparent text-text2 hover:text-text1'
            }`}
          >
            {t === 'products' ? 'Products' : 'Ingredients'}
          </button>
        ))}
      </div>

      {tab === 'ingredients' && (
        <IngredientsTab
          shopId={shopId}
          ingredients={(ingredients ?? []) as StockItem[]}
          products={productList(products)}
          busy={busy}
          onCount={(row) => setAdjustingIngredient({row, mode: 'count'})}
          onWaste={(row) => setAdjustingIngredient({row, mode: 'waste'})}
          onHistory={openIngredientHistory}
          onSave={(item) =>
            item.stockId ? updateIngredient(shopId, item) : createIngredient(shopId, item)
          }
          onDeactivate={(row) => deactivateIngredient(shopId, row.stockId)}
          suppliers={suppliers ?? []}
        />
      )}

      {tab === 'products' && (
        <>
      <div className="flex flex-wrap items-center gap-2 pb-4">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search products…"
          className="nl-input flex-1 w-auto min-w-[200px]"
        />
        <select
          value={modeFilter}
          onChange={(e) => setModeFilter(e.target.value as ModeFilter)}
          className="nl-select w-40"
        >
          <option value="all">All modes</option>
          <option value="unit">Count units</option>
          <option value="prep">Daily prep</option>
          <option value="recipe">Recipe</option>
        </select>
        <div className="flex gap-1">
          {(['all', 'ok', 'low', 'sold_out', 'stale'] as StatusFilter[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={`h-10 rounded-[10px] border px-3 text-xs font-medium transition-colors ${
                statusFilter === s
                  ? 'border-brand bg-brand-soft text-brand'
                  : 'border-borderc text-text2 hover:bg-surface2'
              }`}
            >
              {s === 'all' ? 'All' : STATUS_LABEL[s]} ({counts[s]})
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto rounded-xl border border-borderc">
        {visible.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-text1 font-medium">
              {rows.length === 0 ? 'No products are tracked yet' : 'Nothing matches'}
            </p>
            <p className="text-sm text-text3 mt-1">
              {rows.length === 0
                ? 'Turn on stock tracking for a product from its Stock tracking section.'
                : 'Try a different filter.'}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="sticky top-0 bg-surface2">
              <tr className="border-b border-borderc text-left">
                <th className="px-5 py-3 text-xs font-semibold text-text3">Product</th>
                <th className="px-5 py-3 text-xs font-semibold text-text3">Mode</th>
                <th className="px-5 py-3 text-xs font-semibold text-text3 text-right">On hand</th>
                <th className="px-5 py-3 text-xs font-semibold text-text3 text-right">Par</th>
                <th className="px-5 py-3 text-xs font-semibold text-text3">Status</th>
                <th className="px-5 py-3 text-xs font-semibold text-text3">Updated</th>
                <th className="px-5 py-3 text-xs font-semibold text-text3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <React.Fragment key={row.pid}>
                <tr className="border-b border-borderc hover:bg-surface2">
                  <td className="px-5 py-3 text-sm font-medium text-text1">
                    {row.trackByVariant ? (
                      <button
                        type="button"
                        onClick={() => toggleExpanded(row.pid)}
                        aria-expanded={expanded.has(row.pid)}
                        className="flex items-center gap-1.5 text-left hover:text-brand"
                      >
                        <span className="inline-block w-3 text-text3">
                          {expanded.has(row.pid) ? '▾' : '▸'}
                        </span>
                        {row.productName}
                      </button>
                    ) : (
                      row.productName
                    )}
                    {row.trackByVariant && (
                      <span className="mt-0.5 block pl-[18px] text-[11px] font-normal text-text3">
                        {row.variants.length} variant{row.variants.length === 1 ? '' : 's'}
                        {soldOutSummary(row.variants) ? ` · ${soldOutSummary(row.variants)}` : ''}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-sm text-text2">
                    {MODE_LABEL[row.stockMode] ?? row.stockMode}
                    {row.stockMode === 'recipe' && (
                      <>
                        {/* "limited by X" is not shown: naming the limiting
                            ingredient IS the portions calculation, and the
                            dashboard does not compute stock. The cloud works
                            it out on every recompute but does not yet store
                            it on the product. */}
                        <button
                          type="button"
                          onClick={() => navigate(`/product?edit=${row.pid}`)}
                          className="mt-0.5 block text-[11px] font-medium text-brand hover:opacity-80"
                        >
                          Recipe
                        </button>
                      </>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right font-mono text-sm tabular-nums text-text1">
                    {row.onHand}
                    {row.stockMode === 'recipe' && (
                      <span className="ml-1 text-[11px] font-sans text-text3">portions</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-sm font-mono text-text3 text-right tabular-nums">
                    {row.parLevel ?? '—'}
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLE[row.status]}`}
                    >
                      {STATUS_LABEL[row.status]}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-xs text-text3">
                    {row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '—'}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex justify-end gap-1.5">
                      {row.trackByVariant ? (
                        <button
                          type="button"
                          onClick={() => toggleExpanded(row.pid)}
                          className="nl-btn nl-btn--soft nl-btn--xs"
                        >
                          {expanded.has(row.pid) ? 'Hide variants' : 'Variants'}
                        </button>
                      ) : (
                      <>
                      <button
                        type="button"
                        onClick={() => setAdjusting({row, mode: 'count'})}
                        className="nl-btn nl-btn--soft nl-btn--xs"
                      >
                        Counted
                      </button>
                      {/* A delivery has a supplier and a cost, so it starts a
                          document. The bare purchase event stays for the POS,
                          which receives at the till with no paperwork. */}
                      <button
                        type="button"
                        onClick={() =>
                          navigate(
                            `/stock/intake/new?prefill=product:${encodeURIComponent(row.pid)}`,
                          )
                        }
                        className="nl-btn nl-btn--secondary nl-btn--xs"
                      >
                        Received
                      </button>
                      </>
                      )}
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => openHistory(row)}
                        className="nl-btn nl-btn--secondary nl-btn--xs"
                      >
                        History
                      </button>
                    </div>
                  </td>
                </tr>
                {row.trackByVariant && expanded.has(row.pid) &&
                  row.variants.map((v) => (
                    <tr key={`${row.pid}:${v.variantId}`} className="border-b border-borderc bg-surface2/40">
                      <td className="py-2 pl-12 pr-5 text-sm text-text1">{v.label}</td>
                      <td className="px-5 py-2 text-xs text-text3">Variant</td>
                      <td className="px-5 py-2 text-right font-mono text-sm tabular-nums text-text1">
                        {v.onHand ?? '—'}
                      </td>
                      <td className="px-5 py-2 text-right font-mono text-sm tabular-nums text-text3">
                        {v.par ?? '—'}
                      </td>
                      <td className="px-5 py-2">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                            v.status === 'uncounted' ? STATUS_STYLE.stale : STATUS_STYLE[v.status]
                          }`}
                        >
                          {v.status === 'uncounted' ? 'Not counted' : STATUS_LABEL[v.status]}
                        </span>
                      </td>
                      <td className="px-5 py-2 text-xs text-text3">
                        {v.updatedAt ? new Date(v.updatedAt).toLocaleString() : '—'}
                      </td>
                      <td className="px-5 py-2">
                        <div className="flex justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => setAdjustingVariant({row, variant: v, mode: 'count'})}
                            className="nl-btn nl-btn--soft nl-btn--xs"
                          >
                            Counted
                          </button>
                          <button
                            type="button"
                            onClick={() => setAdjustingVariant({row, variant: v, mode: 'waste'})}
                            className="nl-btn nl-btn--secondary nl-btn--xs"
                          >
                            Waste
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              navigate(
                                `/stock/intake/new?prefill=product:${encodeURIComponent(row.pid)}:${encodeURIComponent(v.variantId)}`,
                              )
                            }
                            className="nl-btn nl-btn--secondary nl-btn--xs"
                          >
                            Received
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => openVariantHistory(row, v)}
                            className="nl-btn nl-btn--secondary nl-btn--xs"
                          >
                            History
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

        </>
      )}

      {adjusting && (
        <StockAdjustModal
          shopId={shopId}
          productId={adjusting.row.pid}
          productName={adjusting.row.productName}
          onHand={adjusting.row.onHand}
          mode={adjusting.mode}
          onClose={() => setAdjusting(null)}
          onDone={onAdjusted}
        />
      )}

      {adjustingVariant && (
        <StockAdjustModal
          shopId={shopId}
          productId={adjustingVariant.row.pid}
          variantId={adjustingVariant.variant.variantId}
          variantLabel={adjustingVariant.variant.label}
          productName={adjustingVariant.row.productName}
          onHand={adjustingVariant.variant.onHand ?? 0}
          mode={adjustingVariant.mode}
          onClose={() => setAdjustingVariant(null)}
          onDone={(message) => {
            setAdjustingVariant(null);
            onAdjusted(message);
          }}
        />
      )}

      {adjustingIngredient && (
        <StockAdjustModal
          shopId={shopId}
          stockId={adjustingIngredient.row.stockId}
          productName={adjustingIngredient.row.itemName}
          onHand={adjustingIngredient.row.onHand}
          unit={adjustingIngredient.row.unit}
          step={stepFor(adjustingIngredient.row.unit)}
          mode={adjustingIngredient.mode}
          onClose={() => setAdjustingIngredient(null)}
          onDone={onIngredientAdjusted}
        />
      )}

      {history && (
        <StockHistoryModal
          productName={history.row.productName}
          events={history.events}
          labels={{}}
          onClose={() => setHistory(null)}
        />
      )}
    </div>
  );
};

export default connect(
  (state: any) => ({
    products: state.product.get('products'),
    ingredients: state.stock.get('ingredients'),
    suppliers: state.stock.get('suppliers'),
    selectedShop: state.dashboard.get('selectedShop'),
  }),
  {
    fetchProducts: Actions.product.fetchProducts,
    fetchIngredients: Actions.stock.fetchIngredients,
    fetchSuppliers: Actions.stock.fetchSuppliers,
    createIngredient: Actions.stock.createIngredient,
    updateIngredient: Actions.stock.updateIngredient,
    deactivateIngredient: Actions.stock.deactivateIngredient,
  },
)(StockLevelsView);
